import React, { useState, useCallback } from "react";
import { api } from "../../utils/api";
import {
  AgentCreateRequest,
  AgentCreateResponse,
  AgentEditRequest,
  AgentEditResponse,
  AgentSavedRequest,
  AgentSavedResponse,
  AgentSpec,
  AgentTemplateFixRequest,
  AgentTemplateFixResponse,
  AgentTestRequest,
  AgentTestResponse,
} from "../../types/agent";

interface FormData {
  subject: string;
  using_feedback: string;
  modal: "text-modal" | "gen-image-modal";
}

interface FormErrors {
  subject?: string;
  using_feedback?: string;
  modal?: string;
}

interface UseAgentReturn {
  formData: FormData;
  errors: FormErrors;
  setErrors: (errors: FormErrors) => void;

  // 생성 관련
  isLoading: boolean;
  createdAgent: AgentSpec | null;
  apiError: string | null;
  showUnsupportedModal: boolean;
  handleInputChange: (field: keyof FormData, value: string) => void;
  createAgent: () => Promise<void>;
  setCreatedAgent: React.Dispatch<React.SetStateAction<AgentSpec | null>>;
  resetForm: () => void;
  
  // 테스트 관련
  testInputValues: Record<string, any>;
  isTestLoading: boolean;
  testResult: AgentTestResponse | null;
  testError: string | null;
  handleTestInputChange: (field: string, value: any) => void;
  testAgent: () => Promise<void>;
  resetTest: () => void;

  // 저장 관련
  isSavedLoading: boolean;
  savedError: string | null;
  saveAgent: (imageData: { url?: string; b64?: string }, categories: string[], childLicense: boolean) => Promise<boolean>;
  resetSaved: () => void;

  // 수정 관련
  isEditLoading: boolean;
  editError: string | null;
  editAgent: (
    imageData: { url?: string; b64?: string }, 
    spec: AgentSpec, 
    childLicense: boolean, 
    categories: string[], 
    hidden: boolean
  ) => Promise<{ success: boolean; updatedTemplate?: string }>;
  resetEdit: () => void;

  // 템플릿 수정 관련
  isTemplateFixLoading: boolean;
  templateFixError: string | null;
  fixTemplate: (spec: AgentSpec, modal: "text-modal" | "gen-image-modal") => Promise<string | null>;
  resetTemplatefix: () => void;
}

export const useAgent = (): UseAgentReturn => {
  const [formData, setFormData] = useState<FormData>({
    subject: "",
    using_feedback: "",
    modal: "text-modal",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // 생성 관련 상태
  const [isLoading, setIsLoading] = useState(false);
  const [createdAgent, setCreatedAgent] = useState<AgentSpec | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showUnsupportedModal, setShowUnsupportedModal] = useState(false);

  // 테스트 관련 상태
  const [testInputValues, setTestInputValues] = useState<Record<string, any>>({});
  const [isTestLoading, setIsTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<AgentTestResponse | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  // 저장 관련 상태
  const [isSavedLoading, setIsSavedLoading] = useState(false);
  const [savedError, setSavedError] = useState<string | null>(null);

  // 수정 관련 상태
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // 템플릿 수정 관련 상태
  const [isTemplateFixLoading, setIsTemplateFixLoading] = useState(false);
  const [templateFixError, setTemplateFixError] = useState<string | null>(null);

  const handleInputChange = useCallback(
    (field: keyof FormData, value: string) => {
      console.log(`handleInputChange - field: ${field}, value: ${value}`);
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    },
    [errors]
  );

  const createAgent = useCallback(async () => {
    setIsLoading(true);
    setApiError(null);
    setCreatedAgent(null);
    setShowUnsupportedModal(false);

    try {
      const requestData: AgentCreateRequest = {
        subject: formData.subject.trim(),
        using_feedback: formData.using_feedback.trim(),
        modal: formData.modal,
      };

      const response = await api.post<AgentCreateResponse>(
        "/admin/v1/agent/gen",
        requestData
      );

      if (response.data.status) {
        if (response.data.unsupport_modal) {
          setShowUnsupportedModal(true);
        } else if (response.data.spec) {
          setCreatedAgent(response.data.spec);
        }
      } else {
        setApiError("Agent 생성에 실패했습니다.");
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Agent 생성 중 오류가 발생했습니다.";
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData({
      subject: "",
      using_feedback: "",
      modal: "text-modal",
    });
    setErrors({});
    setCreatedAgent(null);
    setApiError(null);
    setShowUnsupportedModal(false);
  }, []);

  // 테스트 입력값 변경 함수
  const handleTestInputChange = useCallback((field: string, value: any) => {
    setTestInputValues(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Agent 테스트 함수
  const testAgent = useCallback(async () => {
    if (!createdAgent) {
      setTestError("테스트할 Agent가 없습니다.");
      return;
    }

    setIsTestLoading(true);
    setTestError(null);
    setTestResult(null);

    try {
      const requestData: AgentTestRequest = {
        spec: createdAgent,
        input_values: testInputValues,
      };

      const response = await api.post<AgentTestResponse>(
        "/admin/v1/agent/test",
        requestData
      );

      if (response.data.status) {
        setTestResult(response.data);
      } else {
        setTestError("Agent 테스트에 실패했습니다.");
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Agent 테스트 중 오류가 발생했습니다.";
      setTestError(errorMessage);
    } finally {
      setIsTestLoading(false);
    }
  }, [createdAgent, testInputValues]);

  const resetTest = useCallback(() => {
    setTestInputValues({});
    setTestResult(null);
    setTestError(null);
  }, []);

  const saveAgent = useCallback(async (
    imageData: { url?: string; b64?: string },
    categories: string[], 
    childLicense: boolean
  ): Promise<boolean> => {
    if (!createdAgent) {
      setSavedError("저장할 Agent가 없습니다.");
      return false;
    }

    setIsSavedLoading(true);
    setSavedError(null);

    try {
      const requestData: AgentSavedRequest = {
        spec: createdAgent,
        child_license: childLicense,
        category: categories,
      };

      // 우선순위: URL 방식 > base64 방식
      if (imageData.url) {
        requestData.image_url = imageData.url;
      } else if (imageData.b64) {
        requestData.b64 = imageData.b64;
      } else {
        setSavedError("이미지 데이터가 없습니다.");
        return false;
      }

      const response = await api.post<AgentSavedResponse>(
        "/admin/v1/agent/saved",
        requestData
      );

      if (response.data.status) {
        return true;
      } else {
        setSavedError("Agent 저장에 실패했습니다.");
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Agent 저장 중 오류가 발생했습니다.";
      setSavedError(errorMessage);
    } finally { 
      setIsSavedLoading(false);
    }
    
    return false; 
  }, [createdAgent]);

  const resetSaved = useCallback(() => {
    setSavedError(null);
  }, []);

    // 템플릿 수정 관련 함수
    const fixTemplate = useCallback(async (
      spec: AgentSpec,
      modal: "text-modal" | "gen-image-modal"
    ): Promise<string | null> => {
      setIsTemplateFixLoading(true);
      setTemplateFixError(null);
  
      try {
        const requestData: AgentTemplateFixRequest = {
          spec: spec,
          modal: modal,
        };
  
        const response = await api.post<AgentTemplateFixResponse>(
          "/admin/v1/agent/tmplfix",
          requestData
        );
  
        if (response.data.status && response.data.template) {
          return response.data.template;
        } else {
          setTemplateFixError("템플릿 재수정에 실패했습니다.");
          return null;
        }
    
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Agent 템플릿 수정 중 오류가 발생했습니다.";
        setTemplateFixError(errorMessage);
        return null;
      } finally {
        setIsTemplateFixLoading(false);
      }
    }, []);
  
    const resetTemplatefix = useCallback(() => {
      setTemplateFixError(null);
    }, []);

  // 수정 관련 함수
  const editAgent = useCallback(async (
    imageData: { url?: string; b64?: string },
    spec: AgentSpec,
    childLicense: boolean,
    categories: string[], 
    hidden: boolean
  ): Promise<{ success: boolean; updatedTemplate?: string }> => {
    if (!createdAgent) {
      setEditError("수정할 Agent가 없습니다.");
      return { success: false };
    }
  
    setIsEditLoading(true);
    setEditError(null);
  
    try {
      const editRequestData: AgentEditRequest = {
        id: spec.agent_id,
        category: categories,
        hidden: hidden,
        child_license: childLicense,
        spec: spec, 
      };
  
      if (imageData.url) {
        editRequestData.image_url = imageData.url;
      } else if (imageData.b64) {
        editRequestData.b64 = imageData.b64;
      } else {
        setEditError("이미지 데이터가 없습니다.");
        return { success: false };
      }
  
      const editResponse = await api.post<AgentEditResponse>(
        "/admin/v1/agent/update",
        editRequestData
      );
  
      if (!editResponse.data.status) {
        setEditError("Agent 수정에 실패했습니다.");
        return { success: false };
      }
  
      const newTemplate = await fixTemplate(spec, spec.agent_kind);
      
      if (!newTemplate) {
        console.warn("템플릿 재수정에 실패했지만 Agent 수정은 완료되었습니다.");
      }
  
      return { 
        success: true, 
        updatedTemplate: newTemplate || undefined 
      };
  
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Agent 수정 중 오류가 발생했습니다.";
      setEditError(errorMessage);
      return { success: false };
    } finally {
      setIsEditLoading(false);
    }
  }, [createdAgent, fixTemplate]);

  const resetEdit = useCallback(() => {
    setEditError(null);
  }, []);

  return {
    // 기본 폼 관련
    formData,
    errors,
    setErrors,
    isLoading,
    createdAgent,
    apiError,
    showUnsupportedModal,
    handleInputChange,
    createAgent,
    setCreatedAgent,
    resetForm,
    
    // 테스트 관련
    testInputValues,
    isTestLoading,
    testResult,
    testError,
    handleTestInputChange,
    testAgent,
    resetTest,

    // 저장 관련
    isSavedLoading,
    savedError,
    saveAgent,
    resetSaved,

    // 수정 관련
    isEditLoading,
    editError,
    editAgent,
    resetEdit,

    // 템플릿 수정 관련
    isTemplateFixLoading,
    templateFixError,
    fixTemplate,
    resetTemplatefix,
  };
};
